import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useStatus } from '../context/StatusContext';
import { useDataCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';
import { useDataUpdates } from '../hooks/useDataUpdates';
import LoadingAnimation from '../components/loadingAnimation';
import { getApiBase } from '../utils/apiBase';
import '../styles/validators.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const ValidatorChart = ({ data, title }) => {
    const rawValue = data.total_stake;
    // console.log(`${title} raw value:`, rawValue); // Keep console log for now
    const numValue = Number(rawValue);
    const totalStakeValue = numValue / 1e15;
  
    const isConnected = data.connected === true;
    const uptimePercentage = parseFloat(data.uptime) || 0; // Ensure uptime is a number, default to 0
  
    const chartData = {
        labels: ['Self Bond', 'Delegations', 'Available Space'],
        datasets: [{
          data: [
            Number(data.self_stake) / 1e15,
            Number(data.delegations) / 1e15,
            Number(data.available_space) / 1e15
          ],
          backgroundColor: [
            '#C5B2FF',
            '#A986FF',
            '#DDD4FF'        
          ],
          borderWidth: 0,
          hoverBorderWidth: 0
        }],
      };
  
    const legendItems = chartData.labels.map((label, index) => ({
      label,
      value: chartData.datasets[0].data[index].toFixed(1),
      color: chartData.datasets[0].backgroundColor[index]
    }));

    const options = {
      plugins: {
        legend: {
          display: false  
        },
        tooltip: {
          backgroundColor: 'rgba(31, 41, 55, 0.9)',
          titleFont: {
            family: 'Kanit, sans-serif',
            size: 14
          },
          bodyFont: {
            family: 'Kanit, sans-serif',
            size: 13
          },
          padding: 12,
          cornerRadius: 4
        }
      },
      cutout: '70%',
      // Disable animations to prevent flicker on frequent updates
      animation: false
    };
  
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };
  
    return (
        <div className="validator-chart-container">
          <div className="validator-header">
            <h3>{title}</h3>
            <div className="validator-status">
              <span className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></span>
              <span className="uptime-text">{uptimePercentage.toFixed(2)}%</span>
            </div>
          </div>
          <div className="chart-wrapper">
            <Doughnut data={chartData} options={options} />
            <div className="chart-center-text">
              <div className="total-stake">{totalStakeValue.toFixed(1)}M</div>
              <div className="stake-label">Total Stake</div>
            </div>
          </div>
          <div className="legend-container">
            {legendItems.map((item, index) => (
              <div key={index} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: item.color }}></div>
                <div className="legend-text">
                  <div className="legend-label">{item.label}</div>
                  <div className="legend-value">{item.value}M</div>
                </div>
              </div>
            ))}
          </div>
          <div className="validator-dates">
            <div>Start: {formatDate(data.start_time)}</div>
            <div>End: {formatDate(data.end_time)}</div>
          </div>
        </div>
      );
  };

  function Validators() {
    const [validatorData, setValidatorData] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);  // Add state for current slide
    const [loading, setLoading] = useState(true); // Add loading state
    const [error, setError] = useState(null); // Optional: for error handling display
    const { updatePageStatus } = useStatus();
    const { getCachedData } = useDataCache();
    const gridRef = React.useRef(null); // Add ref for the grid container
    const isInitialLoadRef = useRef(true);
  
    const VALIDATOR_ENDPOINTS = ['/validator_info.json'];
  
    // Handle scroll for pagination - Updated for dynamic slides
    const handleScroll = useCallback(() => {
      if (gridRef.current) {
        const scroller = gridRef.current;
        const actualSlideElements = scroller.querySelectorAll('.validator-chart-container');
  
        if (actualSlideElements.length > 0) {
          const firstSlide = actualSlideElements[0];
          // Assuming all slides have the same width as the first one
          const slideWidth = firstSlide.offsetWidth; 
          const gap = 10; // Match the gap from validators.css for mobile view
          
          if (slideWidth > 0) {
            // Effective width includes the gap between slides in mobile view
            const effectiveSlideWidth = slideWidth + gap; 
            const scrollLeft = scroller.scrollLeft;
            
            // Calculate current slide based on the center of the viewport
            const currentViewCenter = scrollLeft + (scroller.offsetWidth / 2);
            let newSlide = Math.floor(currentViewCenter / effectiveSlideWidth);
            
            // Clamp the value to be within the bounds of available slides
            newSlide = Math.max(0, Math.min(newSlide, actualSlideElements.length - 1));
            
            if (newSlide !== currentSlide) {
              setCurrentSlide(newSlide);
            }
          }
        }
      }
    }, [currentSlide]); // Rerun if currentSlide changes to prevent potential issues
  
    // Register endpoints for caching
    useEffect(() => {
      cacheManager.registerEndpoints('validators', VALIDATOR_ENDPOINTS);
      return () => cacheManager.unregisterEndpoints('validators');
    }, []);
  
    const fetchData = useCallback(() => {
      if (isInitialLoadRef.current) setLoading(true);
      setError(null); // Reset error state
      // console.log('Fetching validator data');
      
      const cachedData = getCachedData('/validator_info.json');
      
      if (cachedData !== null && typeof cachedData === 'object') {
        // console.log('Using cached validator data');
        setValidatorData(cachedData);
        if (isInitialLoadRef.current) setLoading(false);
        // Determine status based on cachedData if necessary
        // For now, assuming StatusEvaluator.validators will be called elsewhere or status is green
        updatePageStatus('validators', 'green'); // Example: set to green if from cache
        return;
      }
  
      const API = getApiBase();
      fetch(`${API}/validator_info.json`, { cache: 'no-store' })
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then(data => {
        // console.log('Received validator data update');
        setValidatorData(data);
        // Potentially use StatusEvaluator here if needed, or rely on global status updates
        updatePageStatus('validators', 'green'); // Example: Set to green on successful fetch
      })
      .catch(error => {
        console.error('Error fetching validator data:', error);
        setError(error.message); // Set error message
        updatePageStatus('validators', 'red');
      })
      .finally(() => {
        if (isInitialLoadRef.current) setLoading(false);
        isInitialLoadRef.current = false;
      });
    }, [updatePageStatus, getCachedData]);
  
    // Add SSE updates
    useDataUpdates(fetchData);
    
    // Initial data fetch
    useEffect(() => {
      // console.log('Validators component mounted, performing initial data fetch');
      fetchData();
      return () => {
        // console.log('Validators component unmounting');
      };
    }, [fetchData]);
  
    if (loading) {
      return <LoadingAnimation />; // Use LoadingAnimation
    }

    if (error) {
      return <div className="error-message">Error: {error}</div>; // Display error message
    }
  
    if (!validatorData) {
      // This case might be hit if loading is false, no error, but data is still null (should be rare if logic is correct)
      return <div className="loading">No validator data available.</div>;
    }
  
    // Get validator keys dynamically
    const validatorKeys = Object.keys(validatorData);
  
    return (
      <div className="validators-page">
        <div 
          ref={gridRef} // Attach ref to the grid container
          className="validators-grid"
          onScroll={handleScroll}
        >
          {/* Dynamically render ValidatorChart components */}
          {validatorKeys.map(key => (
            <ValidatorChart 
              key={key} 
              data={validatorData[key]} 
              title={`${key} Validator`} // Use key for title, adjust if needed
            />
          ))}
        </div>
        {/* Dynamically render pagination dots */}
        {validatorKeys.length > 1 && (
          <div className="pagination-dots-container mobile-only-pagination">
            {validatorKeys.map((_, index) => (
              <div 
                key={index} 
                className={`dot ${currentSlide === index ? 'active' : ''}`} 
              />
            ))}
          </div>
        )}
      </div>
    );
  }
  
  export default Validators;